import { useLocale, useTranslations } from 'next-intl';

import { Logo } from '@/component/Icons/Logo';
import { Link, usePathname } from '@/i18n/routing';
import { FooterMenuPlacementEnum } from '@/__generated__/graphql';
import { navigation } from './constants';
import { useSuspenseQuery } from '@apollo/client';
import { SearchBlogs } from '@/query';
import { ExtendedFooter } from './ExtendedFooter';
import { useParams } from 'next/navigation';
import { NavigationBlock } from './NavigationBlock';

const MenuBlogItem = ({ item }) => {
  const t = useTranslations();

  return (
    <li key={item.title}>
      <Link
        href={`/blogs/${item.blogSlug}/${item.postSlug}`}
        className="text-slate-400 hover:text-slate-700 hover:underline text-base inline-flex"
        prefetch={false}
      >
        {t(item.title)}
      </Link>
    </li>
  );
};

const NavigationItem = ({ item, target = '_self', withBeforeName = false }) => {
  const t = useTranslations();

  return withBeforeName ? (
    <li>
      {item.href && (
        <Link
          href={item.href}
          className="text-slate-400 hover:text-slate-700 hover:underline text-base inline-flex"
        >
          {t(item?.before)} {t(item.name)}
        </Link>
      )}
    </li>
  ) : (
    <li key={item.name}>
      <Link
        href={item.href}
        target={target}
        className="text-slate-400 hover:text-slate-700 hover:underline text-base inline-flex"
        prefetch={false}
      >
        {t(item.name)}
      </Link>
    </li>
  );
};

const Footer = ({ hideFooter, menuItems = [] }) => {
  const locale = useLocale();
  const pathname = usePathname();
  const params = useParams();
  const t = useTranslations();

  const { data: dataBlogs } = useSuspenseQuery(SearchBlogs, {
    variables: { filter: { locale: locale }, limit: 1 },
    errorPolicy: 'ignore',
  });

  const hasExtentedFooter =
    !!(pathname.includes('/destinations/') && params?.groupSlug) ||
    !!(pathname.includes('/listings/') && params?.slug);

  if (hideFooter) {
    return null;
  }

  const defaultFooter = (
    <div className="grid max-[350px]:grid-cols-1 max-[750px]:grid-cols-2 max-[880px]:grid-cols-3 max-[1100px]:grid-cols-4 grid-cols-5 gap-x-8 gap-y-11 md:w-full justify-between">
      <NavigationBlock title={t('menu.fifth')}>
        {navigation.product.map((item) => (
          <NavigationItem item={item} key={item.name} />
        ))}
        {menuItems
          .filter(
            (item) => item.footerMenuPlacement === FooterMenuPlacementEnum.Links
          )
          .map((item) => (
            <MenuBlogItem item={item} key={item.title} />
          ))}
      </NavigationBlock>
      <NavigationBlock title={t('menu.third')}>
        {navigation.host.map((item, i) => (
          <NavigationItem item={item} withBeforeName={true} key={i} />
        ))}
      </NavigationBlock>
      <NavigationBlock title={t('legal')} className="max-[700px]:flex-1">
        {navigation.legal.map((item) => (
          <NavigationItem item={item} target="_blank" key={item.name} />
        ))}
        {menuItems
          .filter(
            (item) => item.footerMenuPlacement === FooterMenuPlacementEnum.Legal
          )
          .map((item) => (
            <MenuBlogItem item={item} key={item.title} />
          ))}
      </NavigationBlock>
      <NavigationBlock title={t('menu.sixth')}>
        {navigation.company.map((item) => (
          <NavigationItem item={item} key={item.name} />
        ))}
        {menuItems
          .filter(
            (item) =>
              item.footerMenuPlacement === FooterMenuPlacementEnum.Company
          )
          .map((item) => (
            <MenuBlogItem item={item} key={item.title} />
          ))}
        {navigation.contacts.map((item, i) => (
          <NavigationItem item={item} withBeforeName={true} key={i} />
        ))}
      </NavigationBlock>
      <NavigationBlock title={t('blog.hero.title')}>
        {(dataBlogs?.blogs?.collection || []).slice(0, 4).map((item) => (
          <li key={item.id}>
            <Link
              href={`/blogs?section=${item.slug}`}
              className="text-slate-400 hover:text-slate-700 hover:underline text-base inline-flex"
              prefetch={false}
            >
              {t(item.name)}
            </Link>
          </li>
        ))}
        {navigation.blogs.map((item, i) => (
          <NavigationItem item={item} withBeforeName={true} key={i} />
        ))}
      </NavigationBlock>
    </div>
  );

  return (
    <footer>
      <div className="container px-16 max-lg:px-10 py-16 border-t border-slate-100 3xl:mt-3 3xl:rounded-t-10">
        <div className="flex max-sm:flex-wrap gap-x-5">
          <div className="space-y-8 md:w-1/5 min-w-[234px] lg:min-w-[340px] max-sm:mb-20">
            <Link aria-label="logo" href="/" prefetch={false}>
              <Logo />
            </Link>
            <div className="space-y-5 mt-6">
              <div className="space-y-[10px]">
                <div className="space-y-1">
                  <div className="text-slate-500 text-sm">
                    {t('company-name')} 5591192678
                  </div>
                  <div className="text-slate-500 text-sm">
                    Lützengatan 12A, 11520 Stockholm
                  </div>
                </div>
                <div className="flex space-x-1 -ml-[6px]">
                  {navigation.social.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      className="text-slate-400 hover:text-teal-600 active:text-slate-500 inline-flex p-[6px]"
                    >
                      <span className="sr-only">{item.name}</span>
                      <item.icon className="h-6 w-6" aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full">
            {!hasExtentedFooter ? (
              defaultFooter
            ) : (
              <ExtendedFooter defaultFooter={defaultFooter} locale={locale} />
            )}
            <div className="flex flex-wrap gap-0.5 text-sm mt-12">
              <Link
                href="/sitemap"
                className="text-slate-500 text-base font-normal underline"
              >
                {t('main.sitemap.title')}
              </Link>
              <a
                className="text-slate-500 text-base font-normal underline ml-3"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  window.openAxeptioCookies && window.openAxeptioCookies();
                }}
              >
                Cookies settings
              </a>
            </div>
          </div>
        </div>
        <div className="mt-10 border-t border-slate-100 pt-8 sm:mt-20 lg:mt-12 flex flex-col gap-3 items-center">
          <span className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Guestit AB. {t('rights-reserved')}
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
